import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import TableCell from "@material-ui/core/TableCell";
import MuiTableHead, {
  TableHeadProps as MuiTableHeadProps
} from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@saleor/components/Checkbox";
import { Node } from "@saleor/types";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";

export interface TableHeadProps extends MuiTableHeadProps {
  colSpan: number;
  disabled: boolean;
  dragRows?: boolean;
  selected?: number;
  hideCheckBox?: boolean;
  items: Node[];
  toolbar?: React.ReactNode | React.ReactNodeArray;
  toggleAll?: (event: any) => void;
  checkBoxSmallWidth?: boolean;
  totalSelected?: number;
  indeterminate?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    cell: {
      padding: 0
    },
    checkBoxSmallWidth: {
      width: "2% !important"
    },
    checkboxSelected: {
      backgroundColor: fade(theme.palette.primary.main, 0.05)
    },
    container: {
      alignItems: "center",
      display: "flex",
      height: 47,
      marginRight: -theme.spacing(2)
    },
    dragRows: {
      padding: 0,
      width: 52
    },
    padding: {
      "&:last-child": {
        padding: 0
      }
    },
    root: {
      backgroundColor: fade(theme.palette.primary.main, 0.05),
      paddingLeft: 0,
      paddingRight: 24
    },
    selectedLabels: {
      "& div": {
        width: "50%"
      },
      display: "flex",
      flexGrow: 1
    },
    spacer: {
      flex: 1
    },
    toolbar: {
      "& > *": {
        marginLeft: theme.spacing(1)
      }
    }
  }),
  { name: "TableHead" }
);

function getColSpan(colSpan: number, dragRows: boolean): number {
  if (dragRows) {
    return colSpan - 2;
  }

  return colSpan - 1;
}

const ProductListTableHead: React.FC<TableHeadProps> = props => {
  const {
    children,
    colSpan,
    disabled,
    dragRows,
    hideCheckBox,
    items,
    selected,
    toggleAll,
    toolbar,
    checkBoxSmallWidth,
    totalSelected,
    indeterminate,
    ...muiTableHeadProps
  } = props;
  const classes = useStyles(props);
  const hasSelected = totalSelected > 0;
  return (
    <MuiTableHead {...muiTableHeadProps}>
      <TableRow>
        {dragRows && (items === undefined || items.length > 0) && (
          <TableCell
            className={classNames({
              [classes.checkboxSelected]: selected
            })}
          />
        )}
        {(items === undefined || items.length > 0) && !hideCheckBox && (
          <TableCell
            padding="checkbox"
            className={classNames({
              [classes.checkboxSelected]: selected,
              [classes.dragRows]: dragRows,
              [classes.checkBoxSmallWidth]: checkBoxSmallWidth
            })}
          >
            <Checkbox
              indeterminate={indeterminate}
              checked={selected && !indeterminate}
              disabled={disabled}
              onChange={toggleAll}
            />
          </TableCell>
        )}
        {totalSelected ? (
          <>
            <TableCell
              className={classNames(classes.root)}
              colSpan={getColSpan(colSpan, dragRows)}
            >
              <div className={classes.container}>
                <div className={classes.selectedLabels}>
                  {selected && selected === 1 ? (
                    <Typography>
                      <FormattedMessage
                        defaultMessage="Selected 1 item"
                        values={{
                          number: selected
                        }}
                      />
                    </Typography>
                  ) : (
                    <Typography>
                      <FormattedMessage
                        defaultMessage="Selected {number} item(s)"
                        values={{
                          number: selected
                        }}
                      />
                    </Typography>
                  )}

                  {hasSelected ? (
                    <Typography>
                      <FormattedMessage
                        defaultMessage="Total Selected {number} items"
                        values={{
                          number: totalSelected
                        }}
                      />
                    </Typography>
                  ) : null}
                </div>
                {toolbar && <div className={classes.toolbar}>{toolbar}</div>}
              </div>
            </TableCell>
          </>
        ) : (
          children
        )}
      </TableRow>
    </MuiTableHead>
  );
};
ProductListTableHead.displayName = "ProductListTableHead";
export default ProductListTableHead;
